import { type ReactNode, useEffect, useId, useRef } from "react";
import { createPortal } from "react-dom";

type DialogProps = {
  title: string;
  onClose: () => void;
  children: ReactNode;
  /** 処理中は Esc で閉じない */
  busy?: boolean;
};

/**
 * モーダルダイアログ（デザインの「05 確認ダイアログ」）。
 * 開いたら最初の操作できる要素にフォーカスし、閉じたら元の要素に戻す。Tab は中で循環させる
 */
export function Dialog({ title, onClose, children, busy = false }: DialogProps) {
  const titleId = useId();
  const ref = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const busyRef = useRef(busy);
  busyRef.current = busy;

  useEffect(() => {
    const previous = document.activeElement as HTMLElement | null;
    const dialog = ref.current;
    const focusables = () =>
      Array.from(
        dialog?.querySelectorAll<HTMLElement>(
          "button:not([disabled]), input:not([disabled]), a[href], [tabindex]:not([tabindex='-1'])"
        ) ?? []
      );
    (focusables()[0] ?? dialog)?.focus();

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busyRef.current) {
        e.preventDefault();
        onCloseRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      const first = items[0];
      const last = items[items.length - 1];
      if (first === undefined || last === undefined) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.body.classList.add("is-modal-open");
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.classList.remove("is-modal-open");
      previous?.focus?.();
    };
  }, []);

  // カード（backdrop-filter）の中に置くと position: fixed がカード基準になるので、body の直下に出す
  return createPortal(
    <div className="dialog-backdrop">
      <div
        ref={ref}
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <h2 id={titleId} className="dialog__title">
          {title}
        </h2>
        {children}
      </div>
    </div>,
    document.body
  );
}

type ConfirmDialogProps = {
  title: string;
  /** 本文 */
  children: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  busy?: boolean;
  error?: string | null;
};

/** 削除などの取り消せない操作の確認。確定ボタンは危険色 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  onConfirm,
  onCancel,
  busy = false,
  error = null,
}: ConfirmDialogProps) {
  return (
    <Dialog title={title} onClose={onCancel} busy={busy}>
      {children}
      {error !== null ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      <div className="dialog__actions">
        <button
          type="button"
          className="button button--secondary"
          onClick={onCancel}
          disabled={busy}
        >
          キャンセル
        </button>
        <button
          type="button"
          className="button button--danger-solid"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? "処理中…" : confirmLabel}
        </button>
      </div>
    </Dialog>
  );
}
